import { app } from "@microsoft/teams-js";
import axios from "axios";
import Config from "../config";
// import { getPlatforms } from "@ionic/react";

/**
 * Client info headers (X-Device-Type, X-Surface, X-App-Version, X-Host-Client)
 * ---------------------------------------------------------------------------
 * Source of truth for detecting client metadata and mapping it
 * to request headers.
 *
 * Where headers are appended (do NOT add these per API call):
 * 1) Default axios.*     → registerDefaultAxiosClientInfoInterceptor() below
 * 2) AxiosInstance       → applyClientInfoHeaders in helpers/AxiosInstance.jsx
 * 3) SecureChatInstance  → request interceptor in components/secure-chat/secure-chat-instance.jsx
 *
 * Excluded: URLs containing "graph.microsoft.com".
 * Loaded early from src/index.tsx so interceptors exist before the first API call.
 */

export type DeviceType = "desktop" | "mobile" | "tablet";

export interface ClientInfo {
    deviceType: DeviceType;
    surface: string;
    hostClient: string;
    hostClientVersion?: string;
    appVersion: string;
}

export type ClientInfoAxiosHeaders = {
    "X-Device-Type": string;
    "X-Surface": string;
    "X-App-Version": string;
    "X-Host-Client": string;
};

const APP_VERSION = Config.VERSION ?? process.env.REACT_APP_VERSION ?? "1.0.0";


let cachedClientInfo: ClientInfo | null = null;
let isFullyResolved = false;
let initPromise: Promise<ClientInfo> | null = null;
let defaultAxiosInterceptorRegistered = false;

const getMatch = (ua: string, regex: RegExp): string => ua.match(regex)?.[1] ?? "";

const detectDeviceType = (ua: string): DeviceType => {
    // iPadOS 13+ often reports as Macintosh with touch support
    const isIpadOs = /Macintosh/i.test(ua) &&
        typeof navigator !== "undefined" &&
        navigator.maxTouchPoints > 1;

    if (isIpadOs || /iPad|Tablet|PlayBook|Silk|Android(?!.*Mobile)/i.test(ua)) {
        return "tablet";
    }
    if (/Android|iPhone|iPod|Mobile/i.test(ua)) {
        return "mobile";
    }
    return "desktop";
};

const detectBrowser = (ua: string): { name: string; version: string } => {
    if (/Edg\/([\d.]+)/.test(ua)) {
        return { name: "edge", version: getMatch(ua, /Edg\/([\d.]+)/) };
    }
    if (/OPR\/([\d.]+)/.test(ua)) {
        return { name: "opera", version: getMatch(ua, /OPR\/([\d.]+)/) };
    }
    if (/Firefox\/([\d.]+)/.test(ua)) {
        return { name: "firefox", version: getMatch(ua, /Firefox\/([\d.]+)/) };
    }
    if (/Chrome\/([\d.]+)/.test(ua)) {
        return { name: "chrome", version: getMatch(ua, /Chrome\/([\d.]+)/) };
    }
    if (/Version\/([\d.]+).*Safari/.test(ua)) {
        return { name: "safari", version: getMatch(ua, /Version\/([\d.]+)/) };
    }
    return { name: "unknown", version: "" };
};

export const getWebClientInfo = (): ClientInfo => {
    const ua = navigator.userAgent;
    const deviceType = detectDeviceType(ua);
    const browser = detectBrowser(ua);
    return {
        deviceType,
        surface: `web-${deviceType}`,
        hostClient: browser.name,
        hostClientVersion: browser.version,
        appVersion: APP_VERSION,
    };
};

export const getTeamsClientInfo = async (): Promise<ClientInfo | null> => {
    try {
        await app.initialize();
        const context = await app.getContext();
        const clientType = context.app.host.clientType;

        let surface = "teams";
        let deviceType: DeviceType = "desktop";

        switch (clientType) {
            case "desktop":
                surface = "teams-desktop";
                deviceType = "desktop";
                break;
            case "web": {
                const width = window.outerWidth < 320 ? window.innerWidth : window.outerWidth;
                const isNarrow = width <= 835 && width !== 0;
                surface = isNarrow ? "teams-mobile-web" : "teams-web";
                deviceType = isNarrow ? "mobile" : "desktop";
                break;
            }
            case "android":
                surface = "teams-android";
                deviceType = "mobile";
                break;
            case "ios":
                surface = "teams-mobile";
                deviceType = "mobile";
                break;
            default:
                surface = `teams-${clientType}`;
                break;
        }

        return {
            deviceType,
            surface,
            hostClient: "teams",
            hostClientVersion: "",
            appVersion: APP_VERSION,
        };
    } catch {
        return null;
    }
};

// for desktop
export const getDesktopAppClientInfo = async (): Promise<ClientInfo> => {
    return {
        deviceType: "desktop",
        surface: "desktop-app",
        hostClient: "electron",
        appVersion: APP_VERSION,
    };
};

// for mobile
// export const getCapacitorClientInfo =async (): Promise<ClientInfo> => {
//     const platforms = getPlatforms();

//     const isAndroid = platforms.includes("android");
//     const isIOS = platforms.includes("ios");
//     const isTablet = platforms.includes("tablet");

//     return {
//         deviceType: isTablet ? "tablet" : "mobile",
//         surface: isAndroid ? "android-app" : "ios-app",
//         hostClient: "capacitor",
//         appVersion: APP_VERSION,
//     };
// }

/** Prefer Teams when available; otherwise fall back to web. Cached after first resolve. */
export const initClientInfo = async (): Promise<ClientInfo> => {
    if (isFullyResolved && cachedClientInfo) {
        return cachedClientInfo;
    }

    if (!initPromise) {
        initPromise = (async () => {
            //web and teams
            const isTeamsRoute = window.location.pathname.includes("/teams/");
            const teamsInfo = isTeamsRoute ? await getTeamsClientInfo() : null;
            cachedClientInfo = teamsInfo ?? cachedClientInfo ?? getWebClientInfo();

            //For the desktop app, uncomment the line below and comment out the above lines.
            // cachedClientInfo = await getDesktopAppClientInfo()
            //For the mobile app, uncomment the line below and comment out the above lines.
            // cachedClientInfo=await getCapacitorClientInfo()

            isFullyResolved = true;
            return cachedClientInfo;
        })();
    }

    return initPromise;
};

/** Sync snapshot for request interceptors (web until Teams init completes). */
export const getClientInfo = (): ClientInfo => {
    if (!cachedClientInfo) {
        cachedClientInfo = getWebClientInfo();
    }
    if (!isFullyResolved) {
        // Warm Teams detection in the background without blocking requests
        void initClientInfo();
    }
    return cachedClientInfo;
};

export const toClientInfoAxiosHeaders = (info: ClientInfo): ClientInfoAxiosHeaders => ({
    "X-Device-Type": info.deviceType,
    "X-Surface": info.surface,
    "X-App-Version": info.appVersion,
    "X-Host-Client": info.hostClient,
});

/** Headers map ready to merge onto an axios request config. */
export const getClientInfoAxiosHeaders = (): ClientInfoAxiosHeaders =>
    toClientInfoAxiosHeaders(getClientInfo());

const shouldSkipClientInfoHeaders = (url = ""): boolean =>
    url.includes("graph.microsoft.com");

/**
 * Applies client-info headers to the default axios instance (all raw axios.* calls).
 * Skips Microsoft Graph. AxiosInstance and SecureChatInstance use their own interceptors.
 */
export const registerDefaultAxiosClientInfoInterceptor = (): void => {
    if (defaultAxiosInterceptorRegistered) {
        return;
    }
    defaultAxiosInterceptorRegistered = true;

    axios.interceptors.request.use((req) => {
        const url = req.url ?? "";
        if (!shouldSkipClientInfoHeaders(url)) {
            Object.assign(req.headers, getClientInfoAxiosHeaders());
        }
        return req;
    });
};

// Warm cache and cover all default-axios endpoints as soon as this module loads.
// void initClientInfo();
// registerDefaultAxiosClientInfoInterceptor();


//If we need the mobile device model, OS version, and manufacturer details.
// import { Device } from '@capacitor/device';
// const info = await Device.getInfo();
// {
// platform: info.platform,
// model: info.model,
// manufacturer: info.manufacturer,
// osVersion: info.osVersion,
// }
// };




// need to ask platform needed or not
export type Platform =
    | "windows"
    | "macos"
    | "linux"
    | "android"
    | "ios"
    | "unknown";

export const detectPlatform = (ua: string): Platform => {

    // iPadOS 13+ reports itself as Macintosh
    const isIpadOs =
        /Macintosh/i.test(ua) &&
        navigator.maxTouchPoints > 1;

    if (isIpadOs || /iPhone|iPad|iPod/i.test(ua)) {
        return "ios";
    }

    if (/Android/i.test(ua)) {
        return "android";
    }

    if (/Windows NT/i.test(ua)) {
        return "windows";
    }

    if (/Macintosh|Mac OS X/i.test(ua)) {
        return "macos";
    }

    if (/Linux/i.test(ua)) {
        return "linux";
    }

    return "unknown";
};